import {
  type ChapterSectionDependency,
  type DependencyKind,
  findDependency,
  generateDependencyId,
} from '@shared/chapterSectionDependencies'
import { useCallback, useMemo, useRef, useState } from 'react'

export function useChapterSectionDependenciesState(initialDeps: ChapterSectionDependency[]) {
  const [draftDeps, setDraftDeps] = useState<ChapterSectionDependency[]>(() => initialDeps.map((d) => ({ ...d })))
  const [pickedFromKey, setPickedFromKey] = useState<string | null>(null)
  const originalRef = useRef<ChapterSectionDependency[]>(initialDeps)

  const dirty = useMemo(() => {
    return JSON.stringify(draftDeps) !== JSON.stringify(originalRef.current)
  }, [draftDeps])

  const pickFrom = useCallback((key: string | null) => {
    setPickedFromKey((prev) => (prev === key ? null : key))
  }, [])

  const cancelFromPick = useCallback(() => {
    setPickedFromKey(null)
  }, [])

  /**
   * Add a dependency edge. Returns the persisted dep (existing or new).
   * If `(fromKey, toKey)` already exists, replaces its reason. Self-loops are rejected.
   */
  const upsertDep = useCallback(
    (fromKey: string, toKey: string, reason: string, kind: DependencyKind): ChapterSectionDependency | null => {
      if (fromKey === toKey) {
        return null
      }
      const trimmedReason = reason.trim()
      let result: ChapterSectionDependency | null = null
      setDraftDeps((prev) => {
        const existing = findDependency(prev, fromKey, toKey)
        if (existing) {
          const updated = { ...existing, reason: trimmedReason, kind }
          result = updated
          return prev.map((d) => (d.id === existing.id ? updated : d))
        }
        const created: ChapterSectionDependency = {
          id: generateDependencyId(),
          fromKey,
          toKey,
          kind,
          reason: trimmedReason,
          createdAt: new Date().toISOString(),
        }
        result = created
        return [...prev, created]
      })
      return result
    },
    [],
  )

  const removeDep = useCallback((depId: string) => {
    setDraftDeps((prev) => prev.filter((d) => d.id !== depId))
  }, [])

  const updateReason = useCallback((depId: string, reason: string) => {
    const trimmed = reason.trim()
    setDraftDeps((prev) => prev.map((d) => (d.id === depId ? { ...d, reason: trimmed } : d)))
  }, [])

  return {
    draftDeps,
    pickedFromKey,
    dirty,
    pickFrom,
    cancelFromPick,
    upsertDep,
    removeDep,
    updateReason,
  }
}
